import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Boxes, Pill, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";

export default function Login() {
  const { login, login2FA, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null); // {challengeToken, email}
  const [code, setCode] = useState("");
  const [empresaLogo, setEmpresaLogo] = useState(null);
  const navigate = useNavigate();

  // ITER174: cargar logo empresa (endpoint público-para logo, no requiere auth)
  useEffect(() => {
    let alive = true;
    api.get("/company/logo")
      .then(r => { if (alive && r?.data?.data_url) setEmpresaLogo(r.data.data_url); })
      .catch(() => { /* sin logo → mantiene Boxes */ });
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.requires2FA) {
      setChallenge({ challengeToken: r.challengeToken, email: r.email });
      return;
    }
    if (r.ok) navigate("/");
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login2FA(challenge.challengeToken, code);
    setLoading(false);
    if (r.ok) navigate("/");
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - decoration */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden" style={{ backgroundColor: "#15241d" }}>
        <div
          className="absolute inset-0 opacity-80"
          style={{
            backgroundImage:
              "url('https://static.prod-images.emergentagent.com/jobs/d5eb69ed-dc27-44cb-80bd-4a8db897652d/images/1c90c2ffa168965a704cfe50a21caeb379a5092e410a5e2fa605a76758048a20.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(21,36,29,0.85), rgba(32,92,69,0.55))" }} />
        <div className="relative z-10 flex flex-col justify-between p-14 text-white w-full">
          <div className="flex items-center gap-3">
            {empresaLogo ? (
              <img src={empresaLogo} alt="Logo empresa" className="w-12 h-12 rounded-lg object-contain bg-white p-1" data-testid="login-logo-empresa" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-[#205c45] grid place-items-center">
                <Boxes size={26} />
              </div>
            )}
            <div>
              <div className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>Pharma Stern</div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#a6cdbd]">Distribuidora Farmacéutica</div>
            </div>
          </div>
          <div className="space-y-6 max-w-md">
            <h1 className="text-4xl xl:text-5xl font-bold leading-tight tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Control total de su distribuidora farmacéutica.
            </h1>
            <p className="text-base text-[#c2d6ce] leading-relaxed">
              Gestione inventarios, lotes, vencimientos FEFO, ventas, cartera y cumplimiento regulatorio desde un solo lugar.
            </p>
            <div className="grid grid-cols-2 gap-3 pt-4">
              <div className="border border-white/10 rounded-md p-3 backdrop-blur-sm bg-white/5">
                <div className="text-[11px] uppercase tracking-widest text-[#a6cdbd]">Lotes</div>
                <div className="text-2xl font-semibold">FEFO</div>
              </div>
              <div className="border border-white/10 rounded-md p-3 backdrop-blur-sm bg-white/5">
                <div className="text-[11px] uppercase tracking-widest text-[#a6cdbd]">Bodegas</div>
                <div className="text-2xl font-semibold">Multi</div>
              </div>
            </div>
          </div>
          <div className="text-xs text-[#a6cdbd]">&copy; {new Date().getFullYear()} · Sistema ERP modular</div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#f7f9f8]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-md bg-[#205c45] grid place-items-center text-white">
              <Pill size={22} />
            </div>
            <div>
              <div className="text-lg font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>Pharma Stern</div>
              <div className="text-[11px] uppercase tracking-widest text-[#5c7368]">Ecuador</div>
            </div>
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {challenge ? "Verificación 2FA" : "Iniciar sesión"}
          </h2>
          <p className="text-sm text-[#4a5c53] mt-2 mb-8">
            {challenge
              ? `Ingrese el código de 6 dígitos de su app autenticadora para ${challenge.email}.`
              : "Acceda con sus credenciales para continuar."}
          </p>

          {!challenge && (
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">
                Correo
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@empresa.com"
                data-testid="login-email-input"
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">
                Contraseña
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={showPassword ? "Tu contraseña" : "••••••••"}
                  data-testid="login-password-input"
                  className="h-11 pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded text-[#5c7368] hover:text-[#205c45] hover:bg-[#e9f2ee] transition-colors"
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {error && (
              <div className="text-sm text-[#b53a25] bg-[#f5d6cf] border border-[#d99584] rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-[#205c45] hover:bg-[#1a4a37] text-white font-semibold"
            >
              {loading ? "Ingresando..." : "Ingresar"}
            </Button>
          </form>
          )}

          {challenge && (
            <form onSubmit={submit2FA} className="space-y-4" data-testid="2fa-form">
              <div className="space-y-1.5">
                <Label htmlFor="code" className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Código 6 dígitos</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  className="h-14 text-center text-2xl tracking-[0.4em] font-mono"
                  data-testid="2fa-code-input"
                  autoFocus
                />
              </div>
              {error && (
                <div className="text-sm text-[#b53a25] bg-[#f5d6cf] border border-[#d99584] rounded-md px-3 py-2" data-testid="2fa-error">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading || code.length < 6} data-testid="2fa-submit-btn"
                      className="w-full h-11 bg-[#205c45] hover:bg-[#1a4a37] text-white font-semibold">
                {loading ? "Verificando..." : "Verificar"}
              </Button>
              <button type="button" className="text-xs text-[#5c7368] hover:underline w-full text-center" onClick={() => { setChallenge(null); setCode(""); }}>
                Cancelar y volver
              </button>
            </form>
          )}

          <div className="mt-8 text-xs text-[#5c7368] border-t border-[#d1dbd6] pt-4">
            Sistema ERP farmacéutico · Solo personal autorizado.
          </div>
        </div>
      </div>
    </div>
  );
}
